import React, { useState, useEffect, useRef } from 'react';
import { Button, Modal, message, Card, Switch, Space, Statistic, Row, Col, Input, Table, Tag, Tooltip } from 'antd';
import { formatCurrency } from '../../utils/formatCurrency';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  UnorderedListOutlined,
  AppstoreOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  PauseCircleOutlined,
  CalendarOutlined,
  HistoryOutlined,
  PlusOutlined,
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { WorkOrder, WorkOrderLineItem } from '../../types';
import KanbanBoard from './KanbanBoard';
import { logEvent, newEventId } from '../../services/eventLogger';
import WorkOrderForm from './WorkOrderForm';
import ActualHoursModal, { ActualHoursEntry } from './ActualHoursModal';
import { useScrollAwareClick } from '../../hooks/useScrollAwareClick';


const WorkOrderList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getClickHandler, touchHandlers } = useScrollAwareClick();
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingWorkOrder, setEditingWorkOrder] = useState<WorkOrder | null>(null);
  const [viewMode, setViewMode] = useState<'kanban' | 'list'>('kanban');
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [workOrderToDelete, setWorkOrderToDelete] = useState<WorkOrder | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [searchParams] = useSearchParams();
  const [selectedStatus, setSelectedStatus] = useState<WorkOrder['status'] | 'all'>(() => {
    const fromUrl = searchParams.get('status');
    const valid: ReadonlyArray<WorkOrder['status']> = [
      'new', 'created', 'scheduled', 'in_progress',
      'waiting_parts', 'waiting_approval', 'completed', 'cancelled',
    ];
    return fromUrl && (valid as ReadonlyArray<string>).includes(fromUrl)
      ? (fromUrl as WorkOrder['status'])
      : 'all';
  });
  const [searchText, setSearchText] = useState('');
  const [actualHoursModalVisible, setActualHoursModalVisible] = useState(false);
  const [pendingCompleteWorkOrderId, setPendingCompleteWorkOrderId] = useState<number | null>(null);
  const [pendingCompleteLineItems, setPendingCompleteLineItems] = useState<WorkOrderLineItem[]>([]);
  const [statusChanging, setStatusChanging] = useState(false);

  // Ref to hold the timeout ID for debouncing
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    fetchWorkOrders();
    // Cleanup timeout on unmount
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const fetchWorkOrders = async (search?: string) => {
    try {
      setLoading(true);

      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const tokens = localStorage.getItem('cnf_auth_tokens');
      let accessToken = '';

      if (tokens) {
        const parsed = JSON.parse(tokens);
        accessToken = parsed.accessToken;
      }

      let url = `${API_BASE_URL}/admin/work-orders`;
      if (search) {
        url += `?search=${encodeURIComponent(search)}`;
      }

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setWorkOrders(data);
      } else {
        message.error('Failed to fetch work orders');
      }
    } catch (error) {
      console.error('Error fetching work orders:', error);
      message.error('Failed to fetch work orders');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchText(value);

    // Debounce logic
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      fetchWorkOrders(value);
    }, 500);
  };

  const handleCreateWorkOrder = () => {
    setEditingWorkOrder(null);
    setShowForm(true);
  };

  const handleEditWorkOrder = (workOrder: WorkOrder) => {
    setEditingWorkOrder(workOrder);
    setShowForm(true);
  };

  const handleViewDetails = (workOrder: WorkOrder) => {
    // Get the current path to determine the role-based route
    const currentPath = location.pathname;
    let basePath = '/admin';

    if (currentPath.startsWith('/owner')) {
      basePath = '/owner';
    } else if (currentPath.startsWith('/advisor')) {
      basePath = '/advisor';
    } else if (currentPath.startsWith('/technician')) {
      basePath = '/technician';
    }

    navigate(`${basePath}/work-orders/${workOrder.id}`);
  };

  const doStatusPatch = async (
    workOrderId: number,
    newStatus: WorkOrder['status'],
    actualHoursEntries?: ActualHoursEntry[]
  ) => {
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    const tokens = localStorage.getItem('cnf_auth_tokens');
    let accessToken = '';
    if (tokens) {
      try { accessToken = JSON.parse(tokens).accessToken; } catch (_) {}
    }

    const body: any = { status: newStatus };
    if (actualHoursEntries && actualHoursEntries.length > 0) {
      body.actual_hours_entries = actualHoursEntries;
    }

    const response = await fetch(`${API_BASE_URL}/admin/work-orders/${workOrderId}/status`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      if (response.status === 409) {
        message.warning(data.detail || 'Hours already recorded for this work order.');
        // Work order is still complete — update local state
        setWorkOrders(prev => prev.map(wo => wo.id === workOrderId ? { ...wo, status: newStatus } : wo));
        return;
      }
      if (response.status === 400) {
        message.error(data.detail || 'Invalid hours value. Must be between 0 and 40.');
        return;
      }
      message.error('Failed to update work order status');
      return;
    }

    const result = await response.json().catch(() => ({}));
    setWorkOrders(prev => prev.map(wo => wo.id === workOrderId ? { ...wo, status: newStatus } : wo));
    message.success('Work order status updated');

    if (newStatus === 'completed') {
      logEvent({
        event_id: newEventId(),
        event_type: 'work_order.completed',
        entity_type: 'work_order',
        entity_id: workOrderId,
        source: 'manual',
      });
    }

    if (result.warnings && result.warnings.includes('actual_hours_recording_failed')) {
      message.warning('Work order completed. Hours could not be saved — please try again later.');
    }
  };

  const handleWorkOrderStatusChange = async (workOrderId: number, newStatus: WorkOrder['status']) => {
    if (newStatus === 'completed') {
      // Find the work order to get its line items
      const workOrder = workOrders.find(wo => wo.id === workOrderId);
      const laborLineItems = (workOrder?.line_items ?? []).filter(
        (item) => item.id !== undefined && (item.labor_hours ?? 0) > 0
      );

      if (laborLineItems.length > 0) {
        // Show actual hours modal
        setPendingCompleteWorkOrderId(workOrderId);
        setPendingCompleteLineItems(laborLineItems);
        setActualHoursModalVisible(true);
        return;
      }
    }

    // Non-complete status or no labor line items: proceed directly
    try {
      setStatusChanging(true);
      await doStatusPatch(workOrderId, newStatus);
    } catch (error) {
      console.error('Error updating work order status:', error);
      message.error('Failed to update work order status');
    } finally {
      setStatusChanging(false);
    }
  };

  const handleActualHoursComplete = async (entries: ActualHoursEntry[]) => {
    if (!pendingCompleteWorkOrderId) return;
    try {
      setStatusChanging(true);
      await doStatusPatch(pendingCompleteWorkOrderId, 'completed', entries);
    } catch (error) {
      console.error('Error completing work order with actual hours:', error);
      message.error('Could not save work order. Please try again.');
    } finally {
      setStatusChanging(false);
      setActualHoursModalVisible(false);
      setPendingCompleteWorkOrderId(null);
      setPendingCompleteLineItems([]);
    }
  };

  const handleActualHoursSkip = async () => {
    if (!pendingCompleteWorkOrderId) return;
    setActualHoursModalVisible(false);
    try {
      setStatusChanging(true);
      await doStatusPatch(pendingCompleteWorkOrderId, 'completed');
    } catch (error) {
      console.error('Error completing work order:', error);
      message.error('Failed to update work order status');
    } finally {
      setStatusChanging(false);
      setPendingCompleteWorkOrderId(null);
      setPendingCompleteLineItems([]);
    }
  };

  const handleFormSuccess = (workOrder: WorkOrder) => {
    setShowForm(false);
    setEditingWorkOrder(null);
    fetchWorkOrders(searchText); // Refresh the list
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingWorkOrder(null);
  };

  const handleDeleteWorkOrder = (workOrder: WorkOrder) => {
    setWorkOrderToDelete(workOrder);
    setDeleteModalVisible(true);
  };

  const confirmDeleteWorkOrder = async () => {
    if (!workOrderToDelete) return;

    setDeleting(true);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const tokens = localStorage.getItem('cnf_auth_tokens');
      let accessToken = '';

      if (tokens) {
        const parsed = JSON.parse(tokens);
        accessToken = parsed.accessToken;
      }

      const response = await fetch(`${API_BASE_URL}/admin/work-orders/${workOrderToDelete.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok || response.status === 204) {
        message.success('Work order deleted successfully');
        setDeleteModalVisible(false);
        setWorkOrderToDelete(null);
        fetchWorkOrders(searchText); // Refresh the list
      } else {
        message.error('Failed to delete work order');
      }
    } catch (error) {
      console.error('Error deleting work order:', error);
      message.error('Failed to delete work order');
    } finally {
      setDeleting(false);
    }
  };

  const handleStatusCardClick = (status: WorkOrder['status'] | 'all') => {
    setSelectedStatus(status);
  };

  const getStatusCounts = () => {
    const counts = {
      new: workOrders.filter(wo => wo.status === 'new' || wo.status === 'created').length,
      scheduled: workOrders.filter(wo => wo.status === 'scheduled').length,
      in_progress: workOrders.filter(wo => wo.status === 'in_progress').length,
      waiting_parts: workOrders.filter(wo => wo.status === 'waiting_parts').length,
      waiting_approval: workOrders.filter(wo => wo.status === 'waiting_approval').length,
      completed: workOrders.filter(wo => wo.status === 'completed').length,
    };
    return counts;
  };

  const statusCounts = getStatusCounts();

  const columns = [
    {
      title: 'WO Number',
      dataIndex: 'wo_number',
      key: 'wo_number',
      render: (text: string) => <span style={{ fontWeight: 600 }}>{text}</span>,
      sorter: (a: WorkOrder, b: WorkOrder) => a.wo_number.localeCompare(b.wo_number),
    },
    {
      title: 'Customer',
      key: 'customer',
      render: (_: any, record: WorkOrder) => {
        if (record.customer) {
          return record.customer.company_name || `${record.customer.first_name} ${record.customer.last_name}`;
        }
        return 'Unknown Customer';
      },
    },
    {
      title: 'Vehicle',
      key: 'vehicle',
      render: (_: any, record: WorkOrder) => {
        if (record.vehicle) {
          return `${record.vehicle.year} ${record.vehicle.make} ${record.vehicle.model}`;
        }
        return 'Unknown Vehicle';
      }
    },
    {
      title: 'Date',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (date: string) => new Date(date).toLocaleDateString(),
      sorter: (a: WorkOrder, b: WorkOrder) => new Date(a.updated_at || '').getTime() - new Date(b.updated_at || '').getTime(),
      defaultSortOrder: 'descend' as const,
    },
    {
      title: 'Total',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (amount: number) => formatCurrency(amount),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={
          status === 'completed' ? 'green' :
            status === 'in_progress' ? 'orange' :
              status === 'waiting_parts' ? 'purple' :
                status === 'waiting_approval' ? 'gold' :
                  status === 'scheduled' ? 'cyan' :
                    'blue'
        }>
          {status.toUpperCase().replace('_', ' ')}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: WorkOrder) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
              size="small"
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleEditWorkOrder(record);
              }}
              size="small"
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteWorkOrder(record);
              }}
              size="small"
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Filter work orders based on selected status for List View as well
  const filteredWorkOrders = selectedStatus === 'all'
    ? workOrders
    : workOrders.filter(wo => wo.status === selectedStatus);

  return (
    <div className="p-4 md:p-6 max-w-full" style={{ maxWidth: '1920px', margin: '0 auto' }}>
      {/* Header Actions */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <h1 className="text-2xl font-bold m-0" style={{ flex: '0 0 auto' }}>Work Orders</h1>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', flex: '1 1 auto', justifyContent: 'flex-end', minWidth: 0 }}>
          <Input
            placeholder="Search work orders..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={handleSearch}
            style={{ flex: '1 1 180px', maxWidth: 300, minWidth: 140 }}
            allowClear
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={() => fetchWorkOrders(searchText)}
            loading={loading}
          />
          <Button icon={<HistoryOutlined />} onClick={() => navigate('/owner/work-orders/archived')}>
            Archive
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateWorkOrder}>
            New
          </Button>
        </div>
      </div>

      {/* Header Stats */}
      <div className="mb-4 md:mb-6">
        <Row gutter={[12, 12]} className="mb-4 md:mb-6" justify="center">
          <Col xs={12} sm={12} md={12} lg={4}>
            <Card
              className="text-center"
              style={{
                padding: '8px 4px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                border: selectedStatus === 'new' ? '2px solid #1890ff' : '1px solid #d9d9d9',
                boxShadow: selectedStatus === 'new' ? '0 4px 12px rgba(24, 144, 255, 0.3)' : 'none',
                transform: selectedStatus === 'new' ? 'scale(1.02)' : 'scale(1)'
              }}
              hoverable
              onClick={() => handleStatusCardClick(selectedStatus === 'new' ? 'all' : 'new')}
            >
              <Statistic
                title="New"
                value={statusCounts.new}
                prefix={<FileTextOutlined className="text-blue-600" style={{ fontSize: '16px' }} />}
                valueStyle={{ color: '#1890ff', fontSize: '20px', fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={12} lg={4}>
            <Card
              className="text-center"
              style={{
                padding: '8px 4px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                border: selectedStatus === 'scheduled' ? '2px solid #13c2c2' : '1px solid #d9d9d9',
                boxShadow: selectedStatus === 'scheduled' ? '0 4px 12px rgba(19, 194, 194, 0.3)' : 'none',
                transform: selectedStatus === 'scheduled' ? 'scale(1.02)' : 'scale(1)'
              }}
              hoverable
              onClick={() => handleStatusCardClick(selectedStatus === 'scheduled' ? 'all' : 'scheduled')}
            >
              <Statistic
                title="Scheduled"
                value={statusCounts.scheduled}
                prefix={<CalendarOutlined className="text-cyan-600" style={{ fontSize: '16px' }} />}
                valueStyle={{ color: '#13c2c2', fontSize: '20px', fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={12} lg={4}>
            <Card
              className="text-center"
              style={{
                padding: '8px 4px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                border: selectedStatus === 'in_progress' ? '2px solid #fa8c16' : '1px solid #d9d9d9',
                boxShadow: selectedStatus === 'in_progress' ? '0 4px 12px rgba(250, 140, 22, 0.3)' : 'none',
                transform: selectedStatus === 'in_progress' ? 'scale(1.02)' : 'scale(1)'
              }}
              hoverable
              onClick={() => handleStatusCardClick(selectedStatus === 'in_progress' ? 'all' : 'in_progress')}
            >
              <Statistic
                title="In Progress"
                value={statusCounts.in_progress}
                prefix={<ClockCircleOutlined className="text-orange-500" style={{ fontSize: '16px' }} />}
                valueStyle={{ color: '#fa8c16', fontSize: '20px', fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={8} lg={4}>
            <Card
              className="text-center"
              style={{
                padding: '8px 4px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                border: selectedStatus === 'waiting_parts' ? '2px solid #722ed1' : '1px solid #d9d9d9',
                boxShadow: selectedStatus === 'waiting_parts' ? '0 4px 12px rgba(114, 46, 209, 0.3)' : 'none',
                transform: selectedStatus === 'waiting_parts' ? 'scale(1.02)' : 'scale(1)'
              }}
              hoverable
              onClick={() => handleStatusCardClick(selectedStatus === 'waiting_parts' ? 'all' : 'waiting_parts')}
            >
              <Statistic
                title="Waiting Parts"
                value={statusCounts.waiting_parts}
                prefix={<PauseCircleOutlined className="text-purple-600" style={{ fontSize: '16px' }} />}
                valueStyle={{ color: '#722ed1', fontSize: '20px', fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={8} lg={4}>
            <Card
              className="text-center"
              style={{
                padding: '8px 4px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                border: selectedStatus === 'waiting_approval' ? '2px solid #faad14' : '1px solid #d9d9d9',
                boxShadow: selectedStatus === 'waiting_approval' ? '0 4px 12px rgba(250, 173, 20, 0.3)' : 'none',
                transform: selectedStatus === 'waiting_approval' ? 'scale(1.02)' : 'scale(1)'
              }}
              hoverable
              onClick={() => handleStatusCardClick(selectedStatus === 'waiting_approval' ? 'all' : 'waiting_approval')}
            >
              <Statistic
                title="Need Approval"
                value={statusCounts.waiting_approval}
                prefix={<ClockCircleOutlined className="text-amber-600" style={{ fontSize: '16px' }} />}
                valueStyle={{ color: '#faad14', fontSize: '20px', fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={8} lg={4}>
            <Card
              className="text-center"
              style={{
                padding: '8px 4px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                border: selectedStatus === 'completed' ? '2px solid #52c41a' : '1px solid #d9d9d9',
                boxShadow: selectedStatus === 'completed' ? '0 4px 12px rgba(82, 196, 26, 0.3)' : 'none',
                transform: selectedStatus === 'completed' ? 'scale(1.02)' : 'scale(1)'
              }}
              hoverable
              onClick={() => handleStatusCardClick(selectedStatus === 'completed' ? 'all' : 'completed')}
            >
              <Statistic
                title="Completed"
                value={statusCounts.completed}
                prefix={<CheckCircleOutlined className="text-green-600" style={{ fontSize: '16px' }} />}
                valueStyle={{ color: '#52c41a', fontSize: '20px', fontWeight: 'bold' }}
              />
            </Card>
          </Col>
        </Row>

        {/* View Mode Toggle and Filter Status */}
        <div className="flex items-center justify-center mb-4" style={{ gap: '16px' }}>
          <Space>
            <span className="text-gray-600">View:</span>
            <Switch
              checked={viewMode === 'kanban'}
              onChange={(checked) => setViewMode(checked ? 'kanban' : 'list')}
              checkedChildren={<AppstoreOutlined />}
              unCheckedChildren={<UnorderedListOutlined />}
            />
          </Space>
          {selectedStatus !== 'all' && (
            <Button
              size="small"
              type="default"
              onClick={() => handleStatusCardClick('all')}
              style={{ fontWeight: 500 }}
            >
              Clear Filter
            </Button>
          )}
        </div>
      </div>

      {/* Main Content */}
      {viewMode === 'kanban' ? (
        <KanbanBoard
          workOrders={workOrders} // Kanban board filters internally or we can pass filteredWorkOrders if needed, but keeping original logic for now
          loading={loading}
          onWorkOrderUpdate={handleEditWorkOrder}
          onWorkOrderDelete={handleDeleteWorkOrder}
          onWorkOrderStatusChange={handleWorkOrderStatusChange}
          onCreateWorkOrder={handleCreateWorkOrder}
          filterStatus={selectedStatus}
        />
      ) : (
        <Card>
          <Table
            columns={columns}
            dataSource={filteredWorkOrders}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 10 }}
            scroll={{ x: 800 }}
            onRow={(record) => ({
              onClick: getClickHandler(() => handleViewDetails(record)),
              ...touchHandlers,
              style: { cursor: 'pointer' }
            })}
          />
        </Card>
      )}

      {/* Work Order Form Modal */}
      <Modal
        title={editingWorkOrder ? 'Edit Work Order' : 'New Work Order'}
        open={showForm}
        onCancel={handleFormCancel}
        footer={null}
        width="95%"
        style={{ maxWidth: 1200, top: 20 }}
        destroyOnHidden
      >
        <WorkOrderForm
          workOrder={editingWorkOrder || undefined}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
          modal
        />
      </Modal>



      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete Work Order"
        open={deleteModalVisible}
        onOk={confirmDeleteWorkOrder}
        onCancel={() => {
          setDeleteModalVisible(false);
          setWorkOrderToDelete(null);
        }}
        okText="Delete"
        okButtonProps={{ danger: true, loading: deleting }}
        cancelText="Cancel"
        confirmLoading={deleting}
        centered
      >
        <p>
          Are you sure you want to delete work order <strong>{workOrderToDelete?.wo_number}</strong>?
        </p>
        <p style={{ color: '#ff4d4f', marginTop: 8 }}>
          This action cannot be undone.
        </p>
      </Modal>
      <ActualHoursModal
        open={actualHoursModalVisible}
        lineItems={pendingCompleteLineItems}
        onComplete={handleActualHoursComplete}
        onSkip={handleActualHoursSkip}
        loading={statusChanging}
      />
    </div>
  );
};

export default WorkOrderList;